import { ChannelRepository, MessageContentType, MessageRepository, UserRepository, Client, API_REGIONS } from "@amityco/ts-sdk";

export const UPTIME_BOT_ID = 'uptime-bot';

export interface IChannel {
    id: string;
    displayName: string;
    lastActivity: Date;
    hasUnreadMessage: boolean;
    members: IMember[];
    profileImage?: string;
    group?: boolean;
}

export interface IMember {
    userId: string;
    displayName: string;
    profileImage?: string;
}

export const getAmityToken = (): string | null => {
    try {
        const storedToken = sessionStorage.getItem('amity_access_token');
        if (storedToken) {
            return storedToken;
        }
        
        return null;
    } catch (error) {
        console.error('Error getting Amity token:', error);
        return null;
    }
};

export const createChannel = async (members: IMember[], ownerId: string): Promise<IChannel> => {
    const createdChannel = await ChannelRepository.createChannel(
        {
            displayName: `${ownerId}-len-${members.length + 1}`,
            type: 'community',
            userIds: [...members.map((member) => member.userId), ownerId],
            metadata: {
                deletedUsers: [],
                group: false,
                users: [...members.map((member) => member.userId), ownerId],
            },
            tags: ["tag"],
        }
    );

    return {
        id: createdChannel.data._id,
        displayName: members.map((member) => member.displayName).join(', '),
        lastActivity: new Date(createdChannel.data.lastActivity ?? ''),
        hasUnreadMessage: false,
        members,
    };
}

export const getMembers = (setMembers: (members: IMember[]) => void): void => {
    let allMembers: IMember[] = [];
    let processedUserIds = new Set<string>();
    let unsubscriber: (() => void) | null = null;

    unsubscriber = UserRepository.getUsers(
        { limit: 100 },
        (userRes) => {
            if (userRes.error) {
                if (unsubscriber) unsubscriber();
                return;
            }

            if (userRes.loading) {
                return;
            }

            const pageMembers = userRes.data
                .filter((user: any) => {
                    if (processedUserIds.has(user.userId)) {
                        return false;
                    }
                    processedUserIds.add(user.userId);
                    return user.metadata.givenName;
                })
                .map((user: any) => ({
                    userId: user.userId,
                    displayName: user.metadata.givenName + ' ' + user.metadata.familyName,
                    profileImage: user.metadata.imageProfile?.fileUrl,
                }));

            allMembers = [...allMembers, ...pageMembers];

            if (userRes.hasNextPage && userRes.onNextPage) {
                userRes.onNextPage();
            } else {
                setMembers(allMembers);
                if (unsubscriber) unsubscriber();
            }
        }
    );
}

export const createTextMessage = async (newMessage: string, channelId: string) => {
    const textMessage = {
        subChannelId: channelId,
        dataType: MessageContentType.TEXT,
        data: {
            text: newMessage,
        }
    };

    const { data: message } = await MessageRepository.createMessage(textMessage);

    return message;
}

export const markAllMessageAsRead = async (channelId: string) => {
    await ChannelRepository.markAsRead(channelId);
}

export const updateUserName = async (userId: string, givenName?: string, familyName?: string, accountId?: string) => {
    try {
        UserRepository.getUser(userId, async response => {
            const user = response.data;
            const metadata = user?.metadata || {};
            if (metadata.givenName === givenName && metadata.familyName === familyName && metadata.rioId === accountId) return;
            if (!givenName || !familyName || !accountId || userId === 'ec6e5df5-b523-4428-bacd-48d04b90a234') return;
            await UserRepository.updateUser(
                userId,
                {
                    metadata: {
                        ...metadata,
                        givenName,
                        familyName,
                        rioId: accountId,
                        accountId,
                        displayName: '',
                    }
                }
            );
        });
    } catch (error) {
        console.error('Erro ao atualizar metadata:', error);
    }
};

export const sendMessageToUser = async (
    message: string,
    targetUserId: string,
    senderUserId: string = UPTIME_BOT_ID
) => {
    let unsubscriber: (() => void) | null = null;

    try {
        let activeClient;
        try {
            activeClient = Client.getActiveClient();
        } catch (error) {
            Client.createClient(import.meta.env.VITE_AMITY_API_KEY, API_REGIONS.US);
            activeClient = Client.getActiveClient();
        }

        const sessionHandler: Amity.SessionHandler = {
            sessionWillRenewAccessToken(renewal: Amity.AccessTokenRenewal) {
                renewal.renew();
            },
        };

        await Client.login(
            { userId: senderUserId, displayName: senderUserId },
            sessionHandler,
        );

        let textMessage = {
            subChannelId: '',
            dataType: MessageContentType.TEXT,
            data: {
                text: message,
            },
        };

        unsubscriber = ChannelRepository.getChannels(
            { membership: 'member', limit: 100 },
            async (channelsData) => {
                if(channelsData.loading) return;

                const existingChannel = channelsData.data?.find((channel: any) =>
                    channel.type === 'community' &&
                    channel.metadata?.users?.includes(targetUserId) &&
                    channel.metadata?.users?.includes(senderUserId) &&
                    channel.metadata?.users?.length === 2
                );
                const channelId = existingChannel?._id;
                if (channelId) {
                    textMessage.subChannelId = channelId;
                    MessageRepository.createMessage(textMessage);
                } else {
                    const channelResponse = await ChannelRepository.createChannel(
                        {
                            displayName: `Chat with ${targetUserId}`,
                            type: 'community',
                            userIds: [senderUserId, targetUserId],
                            metadata: {
                                deletedUsers: [],
                                group: false,
                                users: [senderUserId, targetUserId],
                            },
                            tags: ["private"],
                        }
                    );
                    textMessage.subChannelId = channelResponse.data._id;
                    MessageRepository.createMessage(textMessage);
                }
            }
        );

        return unsubscriber();

    } catch (error) {
        console.error('Error sending message to user:', error);
    }
};

export const sendBotMessage = async (
    message: string,
    targetUserId: string
) => {
    return sendMessageToUser(message, targetUserId, UPTIME_BOT_ID);
};

